// File: /opt/dev_environment/assets/workspace/app-init/src/services/apiClient.js

/**
 * API Client for Vokabelkasten Backend
 * Now includes Bearer token authentication with auto-refresh.
 * All API calls include the Authorization header.
 */

import AuthService from './authService';

const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
const BASE_URL = `${API_BASE}/api/v1`;

class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.status = status;
    this.data = data;
    this.name = 'ApiError';
  }
}

// ---- Token refresh queue to prevent concurrent refreshes ----
let isRefreshing = false;
let refreshSubscribers = [];

function subscribeTokenRefresh(callback) {
  refreshSubscribers.push(callback);
}

function onTokenRefreshed(token) {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
}

function onRefreshFailed() {
  refreshSubscribers.forEach((callback) => callback(null));
  refreshSubscribers = [];
}

/**
 * Get a valid access token, refreshing if necessary.
 * Handles concurrent requests by queuing them during refresh.
 */
async function getValidAccessToken() {
  let accessToken = AuthService.getAccessToken();

  if (accessToken) {
    return accessToken;
  }

  // No valid access token — try to refresh
  const refreshToken = AuthService.getRefreshToken();
  if (!refreshToken) {
    return null; // No auth at all
  }

  if (isRefreshing) {
    // Another request is already refreshing — wait for it
    return new Promise((resolve) => {
      subscribeTokenRefresh((token) => {
        resolve(token);
      });
    });
  }

  // We need to refresh
  isRefreshing = true;

  try {
    accessToken = await AuthService.refreshAccessToken();
    onTokenRefreshed(accessToken);
    return accessToken;
  } catch (error) {
    onRefreshFailed();
    return null;
  } finally {
    isRefreshing = false;
  }
}

/**
 * Core request function with authentication and auto-refresh.
 */
async function request(endpoint, options = {}) {
  const url = `${BASE_URL}${endpoint}`;

  // Get a valid access token
  let accessToken = await getValidAccessToken();

  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    credentials: 'same-origin',
    ...options,
  };

  // Add Authorization header if we have a token
  if (accessToken) {
    config.headers['Authorization'] = `Bearer ${accessToken}`;
  }

  try {
    let response = await fetch(url, config);

    // Handle 401 — try to refresh token and retry once
    if (response.status === 401 && AuthService.getRefreshToken()) {
      try {
        const newToken = await AuthService.refreshAccessToken();
        config.headers['Authorization'] = `Bearer ${newToken}`;

        response = await fetch(url, config);

        if (response.status === 401) {
          // Refresh succeeded but request still fails — session is truly expired
          AuthService.clearTokens();
          window.dispatchEvent(new CustomEvent('auth:session-expired'));
          throw new ApiError('Sitzung abgelaufen. Bitte erneut anmelden.', 401, null);
        }
      } catch (refreshError) {
        if (refreshError instanceof ApiError) throw refreshError;
        AuthService.clearTokens();
        window.dispatchEvent(new CustomEvent('auth:session-expired'));
        throw new ApiError('Sitzung abgelaufen. Bitte erneut anmelden.', 401, null);
      }
    }

    if (response.status === 204) {
      return null;
    }

    const data = await response.json().catch(() => null);

    if (!response.ok) {
      throw new ApiError(
        data?.error || data?.detail || `API error: ${response.status} ${response.statusText}`,
        response.status,
        data
      );
    }

    return data;
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(
      `Network error: ${error.message}`,
      0,
      null
    );
  }
}

function buildQueryString(params) {
  const filtered = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== ''
  );
  if (filtered.length === 0) return '';
  return '?' + filtered.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&');
}

// ============================================
// CARDS API
// ============================================

export const cardsApi = {
  /**
   * List/filter cards.
   * @param {object} params - Query parameters: type, status, compartment, search, verb_group, conjugation_form, base_verb, due_before, sort, order
   */
  async getAll(params = {}) {
    const qs = buildQueryString(params);
    const data = await request(`/cards${qs}`);
    return data;
  },

  /**
   * Get a single card by ID.
   */
  async getById(id) {
    const data = await request(`/cards/${id}`);
    return data;
  },

  /**
   * Create one or more cards.
   * @param {object|object[]} cardData - Single card object or array of card objects
   */
  async create(cardData) {
    const body = Array.isArray(cardData) ? { cards: cardData } : cardData;
    const data = await request('/cards', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return data;
  },

  /**
   * Update a card.
   * @param {string} id - Card ID
   * @param {object} updates - Fields to update
   */
  async update(id, updates) {
    const data = await request(`/cards/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
    return data;
  },

  /**
   * Delete a card.
   * @param {string} id - Card ID
   */
  async delete(id) {
    await request(`/cards/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Record a review and update compartment.
   * @param {string} id - Card ID
   * @param {object} reviewData - { correct: boolean, direction: string }
   */
  async review(id, reviewData) {
    const data = await request(`/cards/${id}/review`, {
      method: 'POST',
      body: JSON.stringify(reviewData),
    });
    return data;
  },

  /**
   * Graduate a card from active to learned.
   * @param {string} id - Card ID
   * @param {object} graduateData - { direction: string }
   */
  async graduate(id, graduateData = {}) {
    const data = await request(`/cards/${id}/graduate`, {
      method: 'POST',
      body: JSON.stringify(graduateData),
    });
    return data;
  },

  /**
   * Get card counts grouped by compartment.
   * @param {object} params - { type?: string }
   */
  async getCounts(params = {}) {
    const qs = buildQueryString(params);
    const data = await request(`/cards/counts${qs}`);
    return data;
  },

  /**
   * Get count of cards due for review.
   * @param {object} params - { type?: string }
   */
  async getDue(params = {}) {
    const qs = buildQueryString(params);
    const data = await request(`/cards/due${qs}`);
    return data;
  },

  /**
   * Check for duplicate verb cards.
   * @param {string[]} keys - Array of verb card keys "baseVerb|conjugationForm|targetPerson"
   */
  async checkDuplicates(keys) {
    const qs = buildQueryString({ keys: keys.join(',') });
    const data = await request(`/cards/check-duplicates${qs}`);
    return data;
  },
};

// ============================================
// REVIEWS API
// ============================================

export const reviewsApi = {
  /**
   * Get review history.
   * @param {object} params - { card_id?, card_type?, since?, limit? }
   */
  async getAll(params = {}) {
    const qs = buildQueryString(params);
    const data = await request(`/reviews${qs}`);
    return data;
  },

  /**
   * Get per-card error rates.
   * @param {object} params - { card_type?, limit?, offset? }
   */
  async getErrorRates(params = {}) {
    const qs = buildQueryString(params);
    const data = await request(`/reviews/error-rates${qs}`);
    return data;
  },
};

// ============================================
// SETTINGS API
// ============================================

export const settingsApi = {
  /**
   * Get current settings.
   */
  async get() {
    const data = await request('/settings');
    return data;
  },

  /**
   * Update settings (partial update).
   * Backend expects PATCH method.
   * @param {object} updates - Fields to update
   */
  async update(updates) {
    const data = await request('/settings', {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
    return data;
  },
};

// ============================================
// DATA MANAGEMENT API
// ============================================

export const dataApi = {
  /**
   * Reset all data.
   * @param {object} options - { confirm: true, reseed?: boolean }
   */
  async reset(options = { confirm: true, reseed: true }) {
    const data = await request('/data/reset', {
      method: 'POST',
      body: JSON.stringify(options),
    });
    return data;
  },

  /**
   * Export all data.
   */
  async exportAll() {
    const data = await request('/data/export');
    return data;
  },
};

export { ApiError, request, getValidAccessToken };